import { inject, Injectable } from '@angular/core';
import {
    EquipmentDto,
    EquipmentFacade,
    InventoryBundleFacade,
    InventoryBundleRead,
    InventoryBundleStore,
} from '@pf/platform-wms/domain';
import { ProfileProvider } from '@pf/shared-common';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    first,
    forkJoin,
    map,
    merge,
    Observable,
    of,
    shareReplay,
    skip,
    switchMap,
} from 'rxjs';
import { EquipmentStatus } from '@control-tower/platform-wms';
import { HandlingUnitsContext } from '../models/handling-units-context.model';
import { isHandlingParts } from '../models/cpw-equipment-types';
import { asArraySafe } from '@pf/shared-utility';
import { uniq } from 'lodash-es';
import { UserPositionFacade } from '@pf/platform-core/domain';
import { LoggerService } from '@pf/shared-services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AsUserFragment } from '@pf/shared/util-platform';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class HandlingUnitAssignmentService {
    private readonly equipmentFacade = inject(EquipmentFacade);
    private readonly profileProvider = inject(ProfileProvider);
    private readonly userPositionFacade = inject(UserPositionFacade);
    private readonly inventoryBundleStore = inject(InventoryBundleStore);
    private readonly inventoryBundleFacade = inject(InventoryBundleFacade);
    private readonly logger = inject(LoggerService);

    assignedHandlingUnits$: Observable<EquipmentDto[]> =
        this.userPositionFacade.active$.pipe(
            map(position => {
                const context = position?.context as HandlingUnitsContext;
                return uniq(asArraySafe(context?.assignedHandlingUnitIds));
            }),
            distinctUntilChanged(),
            switchMap(ids => {
                if (!ids || ids.length === 0) {
                    return of([] as EquipmentDto[]);
                }
                return this.equipmentFacade.getMany$(ids).pipe(
                    switchMap(() => {
                        const equipment$ =
                            this.equipmentFacade.observeIds$(ids);

                        const bundleRefresh$ = equipment$.pipe(
                            switchMap(() => this.observeBundleChanges$(ids))
                        );

                        return merge(equipment$, bundleRefresh$).pipe(
                            map(equipmentList =>
                                equipmentList.map(eq => ({
                                    ...eq,
                                    assignedToUser: AsUserFragment(
                                        this.profileProvider.profile
                                    ),
                                }))
                            )
                        );
                    })
                );
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
            untilDestroyed(this)
        );

    refreshAssignedBundles$(): Observable<void> {
        return this.assignedHandlingUnits$.pipe(
            first(),
            switchMap(equipment => {
                const containerIds = equipment.flatMap(eq =>
                    asArraySafe(eq.containers).map(c => c.id!)
                );
                if (!containerIds.length) return of(void 0);
                return this.inventoryBundleFacade
                    .refreshBundlesForContainers$(containerIds)
                    .pipe(map(() => void 0));
            })
        );
    }

    private observeBundleChanges$(equipmentIds: string[]) {
        return this.inventoryBundleStore
            .filter$((bundle: InventoryBundleRead) => {
                const equipmentId =
                    bundle.container?.equipmentId ||
                    bundle.container?.equipment?.id;

                return equipmentId && equipmentIds.includes(equipmentId);
            })
            .pipe(
                skip(1),
                debounceTime(300),
                switchMap(() => this.equipmentFacade.getMany$(equipmentIds))
            );
    }

    assignHandlingUnit(equipment: EquipmentDto) {
        equipment.status = EquipmentStatus.InUse;
        equipment.assignedToUser = AsUserFragment(this.profileProvider.profile);
        equipment.zone = undefined;

        return this.equipmentFacade.save$(equipment).pipe(
            map(() => true),
            catchError(() => of(false))
        );
    }

    addHandlingUnitById(equipmentId: string) {
        return this.equipmentFacade
            .getById$(equipmentId)
            .pipe(switchMap(equipment => this.addHandlingUnit$(equipment)));
    }

    addHandlingUnit$(equipment: EquipmentDto) {
        const position = this.userPositionFacade.active;
        if (!position?.context) {
            return of(false);
        }

        const context = position.context as HandlingUnitsContext;

        const existingHandlingUnitIds = uniq(
            asArraySafe(context.assignedHandlingUnitIds)
        );

        if (existingHandlingUnitIds.includes(equipment.id)) {
            return of(true);
        }

        const assignedHandlingUnits = uniq([
            ...existingHandlingUnitIds,
            equipment.id,
        ]);

        return this.userPositionFacade
            .updateContext$({
                assignedHandlingUnitIds: assignedHandlingUnits,
                latestHandlingUnitId: equipment.id,
            })
            .pipe(
                switchMap(() => this.assignHandlingUnit(equipment)),
                catchError(() => of(false))
            );
    }

    removeHandlingUnit$(equipment: EquipmentDto) {
        const position = this.userPositionFacade.active;
        if (!position?.context) {
            return of(false);
        }

        const context = position.context as HandlingUnitsContext;
        const newHandlingUnitIds = context.assignedHandlingUnitIds.filter(
            id => id !== equipment.id
        );

        return this.releaseHandlingUnit(equipment.id).pipe(
            switchMap(() =>
                this.userPositionFacade.updateContext$({
                    assignedHandlingUnitIds: newHandlingUnitIds,
                    latestHandlingUnitId: newHandlingUnitIds.length
                        ? newHandlingUnitIds[newHandlingUnitIds.length - 1]
                        : '',
                })
            ),
            map(() => true),
            catchError(() => of(false))
        );
    }

    releaseHandlingUnits$(equipmentIds: string[]) {
        const position = this.userPositionFacade.active;
        if (!position?.context) {
            return of(true);
        }

        const context = position.context as HandlingUnitsContext;
        const idsToRemove = new Set(equipmentIds);
        const newHandlingUnitIds = context.assignedHandlingUnitIds.filter(
            id => !idsToRemove.has(id)
        );

        return this.equipmentFacade.releaseForUser$(equipmentIds).pipe(
            switchMap(() =>
                this.userPositionFacade.updateContext$({
                    assignedHandlingUnitIds: newHandlingUnitIds,
                    latestHandlingUnitId: newHandlingUnitIds.length
                        ? newHandlingUnitIds[newHandlingUnitIds.length - 1]
                        : '',
                })
            ),
            map(() => true),
            catchError(() => of(false))
        );
    }

    releaseAllHandlingUnits$() {
        if (!this.userPositionFacade.active?.context) {
            return of(true);
        }

        return this.releaseHandlingUnits().pipe(
            switchMap(() => {
                return this.userPositionFacade.updateContext$({
                    assignedHandlingUnitIds: [],
                    latestHandlingUnitId: '',
                });
            }),
            map(() => true)
        );
    }

    releaseHandlingUnits() {
        return this.equipmentFacade.releaseForUser$().pipe(
            map(() => true),
            catchError(() => of(false))
        );
    }

    releaseHandlingUnit(equipmentId: string) {
        return this.equipmentFacade.releaseForUser$([equipmentId]);
    }

    releaseEmptyCartEquipment$(
        equipmentIds: string[]
    ): Observable<EquipmentDto[]> {
        if (!equipmentIds.length) {
            return of([]);
        }

        return this.equipmentFacade.getMany$(equipmentIds, true).pipe(
            map(equipmentList =>
                equipmentList.filter(
                    (eq): eq is EquipmentDto =>
                        !!eq &&
                        isHandlingParts(eq.equipmentType) &&
                        asArraySafe(eq.containers).every(
                            container =>
                                !asArraySafe(container.inventoryBundles).some(
                                    bundle => (bundle.totalQuantity ?? 0) > 0
                                )
                        )
                )
            ),
            switchMap(emptyEquipment => {
                if (!emptyEquipment.length) {
                    return of([]);
                }
                return forkJoin(
                    emptyEquipment.map(eq => {
                        eq.status = EquipmentStatus.Available;
                        eq.zone = null;
                        return this.equipmentFacade
                            .save$(eq)
                            .pipe(map(() => eq));
                    })
                );
            }),
            catchError(err => {
                this.logger.error(err);
                return of([]);
            })
        );
    }

    getAssignedHandlingUnits(): EquipmentDto[] {
        const position = this.userPositionFacade.active;
        if (!position?.context) {
            return [];
        }

        const context = position.context as HandlingUnitsContext;
        return asArraySafe(context.assignedHandlingUnitIds)
            .map(equipmentId => this.equipmentFacade.getById(equipmentId))
            .filter(Boolean) as EquipmentDto[];
    }
}
